/**
 * Gamification (`gamification-core` kit) — profile card.
 *
 * Derives player state (XP/level/streak/badges) from the local-first
 * `todo` table, without a new sync column: `computePlayerFromTodos` is a
 * pure and deterministic reducer → instant feedback (works for guests
 * without sync). Positive and optional gamification; private leagues by default.
 *
 * Respects `prefers-reduced-motion`: the XP bar animates with `transition` which
 * the global guard in `global.css` disables; no JS animations.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import { Flame, Sparkles, Trophy } from "@fleet/icons";
import {
  adFreezes,
  computePlayerFromTodos,
  getTodosQuery,
  levelProgress,
  LISTTO_BADGES,
  queryKeys,
  STREAK_PRODUCTIVITY,
} from "@listto/shared";
import type { TodoRecord } from "@listto/powersync";
import { createPowerSyncQuery } from "@listto/shared/solid";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@fleet/ui";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useI18n } from "@/providers/i18n-provider";
import { useAds } from "@/features/growth/ads-context";
import { NativeAdCard } from "@/features/growth/components/native-ad-card";
import { RewardedAdButton } from "@/features/growth/components/rewarded-ad-button";

/** Human title by badge id (reactive i18n). */
function badgeTitle(t: (d: ReturnType<typeof msg>) => string, id: string): string {
  switch (id) {
    case "first-task": {
      return t(msg`First step`);
    }
    case "ten-tasks": {
      return t(msg`Getting started`);
    }
    case "fifty-tasks": {
      return t(msg`Unstoppable`);
    }
    case "century": {
      return t(msg`Centurion`);
    }
    case "week-streak": {
      return t(msg`Productive week`);
    }
    case "month-streak": {
      return t(msg`Month on a roll`);
    }
    case "level-5": {
      return t(msg`Level 5`);
    }
    case "level-10": {
      return t(msg`Level 10`);
    }
    default: {
      return id;
    }
  }
}

interface GamificationSectionProps {
  userId: string;
}

export function GamificationSection(props: GamificationSectionProps) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { t } = useI18n();

  const todos = createPowerSyncQuery<TodoRecord>({
    queryKey: () => queryKeys.todos.list({ scope: "gamification", userId: props.userId }),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getTodosQuery(dbInstance, props.userId) : null;
    },
    enabled: () => Boolean(drizzle() && props.userId),
    throttleTime: 300,
    suspense: false,
  });

  const ads = useAds();
  // Freezes earned by watching a rewarded ad: growth kit feeds
  // `gamification-core` without the engine knowing ads exist.
  const [freezeVersion, bumpFreezes] = createSignal(0, { equals: false });
  const freezes = createMemo(() => {
    freezeVersion();
    return ads ? adFreezes(ads.rewards) : 0;
  });

  const player = createMemo(
    () => computePlayerFromTodos(todos.data ?? [], undefined, freezes()).state,
  );
  const progress = createMemo(() => levelProgress(player().xp));
  const streak = createMemo(() => player().streaks[STREAK_PRODUCTIVITY]?.current ?? 0);
  const owned = createMemo(() => new Set(player().badges));

  const levelLabel = () => {
    const { level } = progress();
    return t(msg`Level ${level}`);
  };
  const xpLabel = () => {
    const { xp } = player();
    return t(msg`${xp} XP`);
  };
  const streakLabel = () => {
    const days = streak();
    return t(msg`${days} day streak`);
  };
  const toNextLabel = () => {
    const xp = progress().xpToNext;
    return t(msg`${xp} XP to next level`);
  };

  return (
    <Card data-testid="gamification-section">
      <CardHeader>
        <div class="flex items-center gap-2">
          <Sparkles class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Progress`)}</CardTitle>
        </div>
      </CardHeader>
      <CardContent class="space-y-6">
        <Show when={!todos.isPending} fallback={<Skeleton class="h-24 w-full rounded-xl" />}>
          {/* Nivel + barra de XP */}
          <div class="space-y-2">
            <div class="flex items-end justify-between">
              <div class="flex items-center gap-2">
                <span
                  class="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary"
                  data-testid="gamification-level"
                >
                  {progress().level}
                </span>
                <div class="flex flex-col">
                  <span class="text-sm font-semibold text-foreground">{levelLabel()}</span>
                  <span class="text-xs text-muted-foreground" data-testid="gamification-xp">
                    {xpLabel()}
                  </span>
                </div>
              </div>
              <Show when={streak() > 0}>
                <span
                  class="flex items-center gap-1 rounded-full bg-warning/10 px-2.5 py-1 text-xs font-semibold text-warning"
                  data-testid="gamification-streak"
                >
                  <Flame class="h-3.5 w-3.5" />
                  {streakLabel()}
                </span>
              </Show>
            </div>
            <div class="h-2.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                class="h-full rounded-full bg-primary transition-[width] duration-500"
                style={{ width: `${Math.round(progress().ratio * 100)}%` }}
              />
            </div>
            <p class="text-right text-xs text-muted-foreground">{toNextLabel()}</p>
          </div>

          {/*
            Momento de recompensa (kit de growth): junto a la racha, la oferta
            opt-in "mira un anuncio → salva tu racha". Con los ads OFF (default)
            no renderiza nada — ni botón, ni SDK, ni request.
          */}
          <div class="flex flex-col gap-3">
            <RewardedAdButton placement="streak-save" onReward={() => bumpFreezes(0)} />
            <NativeAdCard placement="weekly-stats" />
          </div>

          {/* Badges (obtenidos en color, pendientes en gris) */}
          <div>
            <p class="mb-3 flex items-center gap-1.5 text-sm font-medium text-foreground">
              <Trophy class="h-4 w-4 text-muted-foreground" />
              {t(msg`Achievements`)}
            </p>
            <div class="flex flex-wrap gap-2">
              <For each={LISTTO_BADGES}>
                {(badge) => {
                  const has = () => owned().has(badge.id);
                  return (
                    <Tooltip>
                      <TooltipTrigger
                        as="div"
                        class="flex h-11 w-11 items-center justify-center rounded-xl border transition-colors"
                        classList={{
                          "border-primary/30 bg-primary/10 text-primary": has(),
                          "border-dashed border-muted-foreground/20 bg-muted/40 text-muted-foreground/40":
                            !has(),
                        }}
                        data-testid={`badge-${badge.id}`}
                        data-earned={has() ? "true" : "false"}
                        aria-label={badgeTitle(t, badge.id)}
                      >
                        <Trophy class="h-5 w-5" />
                      </TooltipTrigger>
                      <TooltipContent>{badgeTitle(t, badge.id)}</TooltipContent>
                    </Tooltip>
                  );
                }}
              </For>
            </div>
          </div>
        </Show>
      </CardContent>
    </Card>
  );
}
